import { useCallback, useState } from "react";
import { parseApiError } from "@/lib/error-handling";
import type { ToastMessage } from "@/lib/ui";
import type {
  AdminParticipantDetail,
} from "@/lib/admin-api";

type ShowToast = (message: ToastMessage) => void;

const EMPTY_BODY: Record<string, never> = {};

export function useParticipantActions(args: {
  participantId: string | undefined;
  participant: AdminParticipantDetail | null;
  approve: {
    mutateAsync: (args: {
      params: { path: { id: string } };
      body: Record<string, never>;
    }) => Promise<unknown>;
  };
  reject: { mutateAsync: (args: { params: { path: { id: string } }; body: { reason: string } }) => Promise<unknown> };
  cancel: { mutateAsync: (args: { params: { path: { id: string } }; body: { reason: string; refund?: boolean } }) => Promise<unknown> };
  showToast: ShowToast;
}) {
  const { participantId, participant, approve, reject, cancel, showToast } = args;
  const [actionLoading, setActionLoading] = useState<"approve" | "reject" | "cancel" | null>(null);

  const canApprove = participant?.status === "PENDING";
  const canReject = participant?.status === "PENDING";
  const canCancel =
    participant?.status === "PENDING" || participant?.status === "APPROVED";

  const handleApprove = useCallback(async (): Promise<boolean> => {
    if (!participantId || !participant) return false;
    setActionLoading("approve");
    try {
      await approve.mutateAsync({
        params: { path: { id: participantId } },
        body: EMPTY_BODY,
      });
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant approved",
      });
      return true;
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participant",
      });
      return false;
    } finally {
      setActionLoading(null);
    }
  }, [approve, participant, participantId, showToast]);

  const handleReject = useCallback(async (reason: string): Promise<boolean> => {
    if (!participantId || !participant) return false;
    const trimmed = reason.trim();
    if (trimmed === "") return false;

    setActionLoading("reject");
    try {
      await reject.mutateAsync({
        params: { path: { id: participantId } },
        body: { reason: trimmed },
      });
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant rejected",
      });
      return true;
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to reject participant",
      });
      return false;
    } finally {
      setActionLoading(null);
    }
  }, [participantId, participant, reject, showToast]);

  const handleCancel = useCallback(async (args: { reason: string; refund: boolean }): Promise<boolean> => {
    if (!participantId || !participant) return false;
    const trimmed = args.reason.trim();
    if (trimmed === "") return false;

    setActionLoading("cancel");
    try {
      const participantWithPayments = participant as {
        status?: string;
        stripePaymentIntentId?: string | null;
        sharedPaymentIntentId?: string | null;
        depositStripePaymentIntentId?: string | null;
        depositStripePaymentIntentStatus?: string | null;
        sharedDepositPaymentIntentId?: string | null;
        sharedDepositPaymentIntentStatus?: string | null;
      };
      const hasBalancePayment =
        participantWithPayments.status === "APPROVED" &&
        (Boolean(participantWithPayments.stripePaymentIntentId) ||
          Boolean(participantWithPayments.sharedPaymentIntentId));
      const hasDepositPayment =
        participantWithPayments.status === "APPROVED" &&
        ((Boolean(participantWithPayments.depositStripePaymentIntentId) &&
          participantWithPayments.depositStripePaymentIntentStatus ===
            "succeeded") ||
          (Boolean(participantWithPayments.sharedDepositPaymentIntentId) &&
            participantWithPayments.sharedDepositPaymentIntentStatus ===
              "succeeded"));
      const hasPaymentCaptured = hasBalancePayment || hasDepositPayment;
      const requestBody: { reason: string; refund?: boolean } = { reason: trimmed };
      if (hasPaymentCaptured) {
        requestBody.refund = args.refund;
      }

      await cancel.mutateAsync({
        params: { path: { id: participantId } },
        body: requestBody,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant cancelled",
      });
      return true;
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to cancel participant",
      });
      return false;
    } finally {
      setActionLoading(null);
    }
  }, [cancel, participant, participantId, showToast]);

  return {
    actionLoading,
    canApprove,
    canReject,
    canCancel,
    handleApprove,
    handleReject,
    handleCancel,
  };
}


